/**
 * CanvasRenderer - Unified read-only canvas renderer
 *
 * Supports two data sources:
 * - blockId: Fetches resolved data via useResolvedBlock
 * - resolvedBlock: Pre-resolved data (skips fetch, used by reference visuals)
 *
 * Supports two modes:
 * - static: Thumbnails, previews (no interactivity)
 * - animated: Animation playback with timeline
 *
 * NOTE: Editor mode uses separate PlayCanvas component (pixel-based with zoom/pan)
 */

import { useMemo } from 'react'
import { useResolvedBlock } from '@/hooks/UseResolvedBlock'
import { BlockRenderProvider } from '@/components/blocks/BlockRenderContext'
import { BlockRenderer } from '@/components/blocks/BlockRenderer'
import { FieldMarkers } from '@/components/field/FieldMarkers'
import { drawingBlockToDrawing } from '@/adapters/DrawingAdapter'
import { viewBoxFlipY } from '@/utils/Coordinates'
import type { RenderMode, CoordinateTransform, ScaleFactors } from '@/components/blocks/Types'
import type { PlayBlock_Union, ResolvedBlock } from '@/types/blocks'

export interface CanvasRendererProps {
	blockId?: string
	resolvedBlock?: ResolvedBlock
	mode: 'static' | 'animated'
	viewBox: { width: number; height: number }

	// Optional configuration
	showField?: boolean // Default: true
	scale?: Partial<ScaleFactors>
	animationTime?: number // 0-1 for animated mode
	className?: string
}

export function CanvasRenderer({
	blockId,
	resolvedBlock: preResolved,
	mode,
	viewBox,
	showField = true,
	scale,
	animationTime = 0,
	className,
}: CanvasRendererProps) {
	// Fetch only when blockId is provided and no pre-resolved data
	const { resolvedBlock: fetchedBlock, isLoading, error } = useResolvedBlock(
		blockId ?? null,
		!preResolved,
	)

	const resolvedBlock = preResolved ?? fetchedBlock

	// Create coordinate transform for viewBox (Y-flip only)
	const coordinateTransform = useMemo<CoordinateTransform>(
		() => (point) => viewBoxFlipY(point, viewBox.height),
		[viewBox.height],
	)

	// Memoize render context value to prevent unnecessary re-renders
	const renderContextValue = useMemo(
		() => ({
			mode: mode as RenderMode,
			zoom: 100,
			coordinateTransform,
			viewBox,
			scale,
			animationTime: mode === 'animated' ? animationTime : undefined,
		}),
		[mode, coordinateTransform, viewBox, scale, animationTime],
	)

	// Collect all renderable blocks (players, drawings, annotations) for SVG
	// Must be called before early returns (React Hooks rule)
	const svgBlocks = useMemo(() => {
		if (!resolvedBlock) return []

		const blocks: PlayBlock_Union[] = []

		// Order matters: SVG renders later elements on top
		// Drawings first (underneath), players on top, annotations topmost
		resolvedBlock.drawings.forEach((d) => {
			// Convert ResolvedDrawing to canvas Drawing and attach to block for rendering
			const canvasDrawing = drawingBlockToDrawing(d)
			const drawingWithResolved = {
				...d.drawing,
				properties: {
					...d.drawing.properties,
					__resolved_drawing: canvasDrawing,
				},
			}
			blocks.push(drawingWithResolved as PlayBlock_Union)
		})
		resolvedBlock.players.forEach((p) => blocks.push(p))
		resolvedBlock.annotations.forEach((a) => blocks.push(a))

		return blocks
	}, [resolvedBlock])

	if (!preResolved && isLoading) {
		return (
			<div
				className={className}
				style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
			>
				<span style={{ color: 'var(--muted-foreground)' }}>Loading...</span>
			</div>
		)
	}

	if (!preResolved && (error || !resolvedBlock)) {
		console.error(`[CanvasRenderer] Failed to load block blockId=${blockId}`, error?.message ?? 'no data returned')
		return (
			<div
				className={className}
				style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
			>
				<span style={{ color: 'var(--destructive)' }}>Failed to load block</span>
			</div>
		)
	}

	return (
		<div className={className} style={{ position: 'relative', width: '100%', height: '100%' }}>
			{/* SVG Layer - players, drawings, annotations */}
			<svg
				viewBox={`0 0 ${viewBox.width} ${viewBox.height}`}
				preserveAspectRatio="xMidYMid meet"
				style={{ width: '100%', height: '100%' }}
			>
				{/* Field background */}
				{showField && (
					<FieldMarkers
						width={viewBox.width}
						height={viewBox.height}
						transformPoint={coordinateTransform}
					/>
				)}

				{/* SVG block rendering */}
				<BlockRenderProvider value={renderContextValue}>
					{svgBlocks.map((block) => (
						<BlockRenderer key={block.id} block={block} />
					))}
				</BlockRenderProvider>
			</svg>
		</div>
	)
}
